import Axios, { AxiosError, AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { apiInstance } from "./apiInstance";
import { parseJwt } from "../lib/parseToken";
import { publicPaths } from "@/lib/utils";

/* ---------------- TOKEN ACTIONS ---------------- */
export function setToken(token: string) {
  apiInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  sessionStorage.setItem("authToken", token);
}

export function removeToken() {
  delete apiInstance.defaults.headers.common["Authorization"];
  sessionStorage.removeItem("authToken");
}

export function getToken() {
  const headerToken = apiInstance.defaults.headers.common["Authorization"];
  if (typeof headerToken === "string") return headerToken.split(" ")[1];

  const storedToken = sessionStorage.getItem("authToken");
  if (storedToken) {
    apiInstance.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
    return storedToken;
  }
  return null;
}

/* ---------------- TOKEN VALIDATION ---------------- */
export function isTokenValid() {
  try {
    const token = getToken();
    if (!token) return false;

    const jwt = parseJwt(token);
    if (!jwt || !jwt.exp) return false;

    const now = Math.floor(Date.now() / 1000);
    return now < jwt.exp;
  } catch {
    return false;
  }
}

/* ---------------- LOGOUT ---------------- */
export function logoutUser() {
  removeToken();
  window.location.href = "/login";
}

/* ---------------- AXIOS INTERCEPTORS ---------------- */
export async function handleRequest(request: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  request.headers = request.headers instanceof AxiosHeaders ? request.headers : new AxiosHeaders(request.headers);
  if (typeof window !== "undefined" && publicPaths.some((path) => window.location.pathname.includes(path))) return request;
  const token = getToken();
  if (!token || !isTokenValid()) {
    logoutUser();
    return Promise.reject(new Axios.Cancel("Invalid token"));
  }
  request.headers.set("Authorization", `Bearer ${token}`);
  return request;
}

export function handleResponse(response: AxiosResponse): AxiosResponse {
  return response;
}

export async function handleApiError(error: AxiosError) {
  if (Axios.isCancel(error)) throw error;
  if (!error.response) throw error;

  if (error.response.status === 401) logoutUser();

  throw error;
}
